import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Newspaper, ArrowRight, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import MarketClosedBanner from '@/components/MarketClosedBanner';
import NewsArticleImage from '@/components/NewsArticleImage';
import NewsWatchStar from '@/components/NewsWatchStar';
import { Link } from 'react-router-dom';
import {
  ProviderSentimentChip,
  NewsSentimentSummary,
  MassiveInsightReasoning,
} from '@/components/news/NewsBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Poll every 60s while the strip is mounted — matches the leaderboard
// TopMoversWidget's cadence so the news feed shifts with the movers.
const POLL_MS = 60_000;

// Session-scoped cache so navigating back to the leaderboard from
// e.g. `/news` or `/how-to` doesn't repull 40-60 tickers of news
// just to show the exact same content. Fresh picks are re-randomized
// from the cached article pool on each mount so the user still sees
// visual variety even when the data hasn't changed.
const CACHE_KEY = 'dividrip:moversStrip:cache';
const CACHE_TTL_MS = 5 * 60_000;

function readStripCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch { return null; }
}

function writeStripCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, savedAt: Date.now() }));
  } catch { /* quota / private mode — ignore */ }
}

/** Same kept-tickers + per-ticker article count → treat as unchanged. */
function isSameStripContent(a, b) {
  if (!a || !b) return false;
  const at = (a.kept_tickers || []).join(',');
  const bt = (b.kept_tickers || []).join(',');
  if (at !== bt) return false;
  const aa = a.by_ticker || {};
  const bb = b.by_ticker || {};
  for (const t of a.kept_tickers || []) {
    if ((aa[t]?.count || 0) !== (bb[t]?.count || 0)) return false;
  }
  return true;
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    const days = Math.floor(diff / 86400);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

// Fisher–Yates in place.
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Pick 3 articles for the leaderboard strip (Sep 2026 rewrite):
 *   • Three different tickers AND three different stories — a sector piece
 *     that tags TECK + COPX + SCCO counts once (dedupe on url / title / image).
 *   • Composition by the stock's actual move: 2 gainers + 1 loser.
 *     - 1 gainer only → 1 gainer + 2 losers; 0 gainers → 3 losers;
 *       0 losers → 3 gainers. Without quotes, sentiment stands in.
 *   • Prefer Massive; random within each bucket.
 */
const storyKey = (a) => (a.url || '').split('?')[0].toLowerCase()
  || (a.title || '').toLowerCase().replace(/\s+/g, ' ').trim()
  || a.image_url || '';

function pickThreeForStrip(all, quotes = {}) {
  if (!Array.isArray(all) || all.length === 0) return [];
  const providerRank = { massive: 0, freenews: 1, yahoo: 1, finnhub: 2 };
  const better = (a, prev) => {
    const pRank = providerRank[a.provider] ?? 9;
    const prevRank = providerRank[prev.provider] ?? 9;
    return pRank < prevRank || (pRank === prevRank && (a.published_utc || '') > (prev.published_utc || ''));
  };

  // 1) One article per ticker.
  const byTicker = new Map();
  for (const a of all) {
    const prev = byTicker.get(a.ticker);
    if (!prev || better(a, prev)) byTicker.set(a.ticker, a);
  }
  // 2) One ticker per story (drop duplicates of the same headline/url/image).
  const byStory = new Map();
  for (const a of byTicker.values()) {
    const k = storyKey(a);
    const prev = byStory.get(k);
    if (!prev || better(a, prev)) byStory.set(k, a);
  }
  const unique = Array.from(byStory.values());

  // 3) Gainers / losers by real move; sentiment if no quote.
  const dir = (a) => {
    const chg = Number((quotes || {})[a.ticker]?.change_pct);
    if (Number.isFinite(chg) && chg !== 0) return chg > 0 ? 'up' : 'down';
    if (a.sentiment === 'Bullish') return 'up';
    if (a.sentiment === 'Bearish') return 'down';
    return 'flat';
  };
  const up = shuffle(unique.filter((a) => dir(a) === 'up'));
  const down = shuffle(unique.filter((a) => dir(a) === 'down'));
  const flat = shuffle(unique.filter((a) => dir(a) === 'flat'));

  let want = { up: 2, down: 1 };
  if (up.length === 0) want = { up: 0, down: 3 };
  else if (up.length === 1) want = { up: 1, down: 2 };
  else if (down.length === 0) want = { up: 3, down: 0 };

  const picks = [...up.slice(0, want.up), ...down.slice(0, want.down)];
  const leftover = [...flat, ...up.slice(want.up), ...down.slice(want.down)];
  while (picks.length < 3 && leftover.length > 0) picks.push(leftover.shift());
  return picks.slice(0, 3);
}

function fmtPrice(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  if (!isFinite(n)) return null;
  return n.toFixed(2);
}

function ChangeBadge({ quote }) {
  if (!quote) return null;
  const chg = Number(quote.change_pct);
  if (!isFinite(chg)) return null;
  const up = chg >= 0;
  const cls = up
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-rose-600 dark:text-rose-400';
  const Arrow = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold font-mono ${cls}`}
          data-testid="movers-news-tile-change">
      <Arrow className="w-3 h-3" />
      {up ? '+' : ''}{chg.toFixed(2)}%
    </span>
  );
}

function ArticleTile({ article, quote, branding }) {
  const open = () => {
    if (article.url) window.open(article.url, '_blank', 'noopener,noreferrer');
  };
  const hasPx = Number(quote?.price) > 0;
  const pcStr = hasPx ? fmtPrice(quote?.prev_close) : null;
  const openStr = hasPx && !quote?.open_is_prev_close ? fmtPrice(quote?.open) : null;
  const priceStr = hasPx ? fmtPrice(quote?.price) : null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      className="group text-left rounded-lg border bg-card hover:border-primary/50 transition-colors overflow-hidden flex flex-col cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      data-testid={`movers-news-tile-${article.ticker}`}
    >
      <NewsArticleImage article={article} branding={branding} className="w-full aspect-[16/9]" variant="tile" />
      <div className="p-3 flex-1 flex flex-col gap-1">
        {/* Row 1: ticker · div/non-div tag · sentiment · price change */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <NewsWatchStar ticker={article.ticker} isDividend={quote?.is_dividend} variant="chip" />
          <Link
            to={`/stock/${article.ticker}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border border-primary/40 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary hover:-translate-y-px active:translate-y-0 transition-[background-color,color,transform,border-color] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            title={`Open the ${article.ticker} stock page`}
            data-testid={`movers-news-tile-ticker-${article.ticker}`}
          >
            {article.ticker}
            <ExternalLink aria-hidden className="w-2.5 h-2.5 opacity-70" />
          </Link>
          {quote && quote.is_dividend !== undefined && (
            quote.is_dividend ? (
              <span
                className="text-[9px] font-bold px-1 py-0.5 rounded bg-sky-500/15 text-sky-700 dark:text-sky-300 uppercase tracking-wider"
                data-testid={`movers-news-tile-divtag-${article.ticker}`}
              >
                Div
              </span>
            ) : (
              <span
                className="text-[9px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 uppercase tracking-wider"
                data-testid={`movers-news-tile-divtag-${article.ticker}`}
              >
                Non-Div
              </span>
            )
          )}
          <ProviderSentimentChip provider={article.provider} sentiment={article.sentiment} compact />
          <ChangeBadge quote={quote} />
        </div>
        {/* Row 2: open/current price on the left, publisher + age on the right */}
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground flex-wrap">
          {priceStr && (
            <span className="font-mono" title="PC = previous close (the % change is vs. this) · OP = today's open · P = current price">
              {pcStr && <>PC: <span className="text-foreground">${pcStr}</span>{' '}</>}
              {openStr && <>OP: <span className="text-foreground">${openStr}</span>{' '}</>}
              P: <span className="text-foreground">${priceStr}</span>
            </span>
          )}
          <span className="truncate ml-auto flex items-center gap-1">
            <span className="truncate">{article.publisher}</span>
            <span>·</span>
            <span className="whitespace-nowrap">{timeAgo(article.published_utc)}</span>
            <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </span>
        </div>
        <h4 className="text-sm font-bold leading-snug line-clamp-2 group-hover:text-primary transition-colors">
          {article.title}
        </h4>
        {article.provider === 'massive' && article.insight_reasoning ? (
          <MassiveInsightReasoning article={article} compact />
        ) : article.description ? (
          <p className="text-xs text-muted-foreground leading-snug line-clamp-3 mt-1"
             data-testid="movers-news-tile-description">
            {article.description}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default function MoversNewsStrip() {
  // Hydrate synchronously from session cache. `pickThreeForStrip` is
  // called on the cached articles so returning users see a fresh set
  // of tiles even when the underlying data hasn't changed.
  const cachedOnMount = typeof window !== 'undefined' ? readStripCache() : null;
  const [data, setData] = useState(cachedOnMount ? cachedOnMount.data : null);
  const [picks, setPicks] = useState(() =>
    cachedOnMount ? pickThreeForStrip(cachedOnMount.data.articles || [], cachedOnMount.data.quotes || {}) : []
  );
  const [loading, setLoading] = useState(!cachedOnMount);
  // Preserve the timestamp of the actual last fetch (from cache) so
  // the "Refreshed Xs ago" label doesn't jump backwards when the
  // background poll finishes with the same data.
  const [refreshedAt, setRefreshedAt] = useState(
    cachedOnMount ? cachedOnMount.savedAt : null
  );
  const [, setNowTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const url = `${API}/news/movers?use_top_movers=1&top_movers_limit=20&limit_per_ticker=50&_ts=`;
    const load = async () => {
      try {
        const res = await axios.get(url + Date.now(), { withCredentials: true });
        if (cancelled) return;
        const fresh = res.data;
        writeStripCache(fresh);
        // Re-pick tiles on every poll so the strip keeps rotating each
        // minute even while the server serves the same 5-min cached pool.
        setPicks(pickThreeForStrip(fresh.articles || [], fresh.quotes || {}));
        setRefreshedAt(Date.now());
        setData((prev) => (isSameStripContent(prev, fresh) ? prev : fresh));
        setLoading(false);
      } catch {
        if (cancelled) return;
        setData((prev) => prev || { enabled: false, articles: [] });
        setLoading(false);
      }
    };
    load();
    const pollId = setInterval(load, POLL_MS);
    // Tick every second so the "Refreshed Xs ago" label counts up
    // smoothly instead of jumping.
    const tickId = setInterval(() => setNowTick((n) => n + 1), 1_000);
    return () => { cancelled = true; clearInterval(pollId); clearInterval(tickId); };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-6 text-2xl text-muted-foreground"
           data-testid="movers-news-strip-loading">
        <Loader2 className="w-7 h-7 animate-spin text-primary" />
        <span className="font-bold tracking-wider">LOADING NEWS</span>
      </div>
    );
  }
  if (!data || data.enabled === false) return null;
  if (picks.length === 0) return null;

  return (
    <div className="rounded-xl border border-primary/45 bg-card p-4 sm:p-5" data-testid="movers-news-strip">
      <Link
        to="/news"
        className="mb-2 -mx-1 px-1 py-1 flex items-center justify-between gap-3 flex-wrap rounded hover:bg-muted/40 transition-colors group cursor-pointer"
        data-testid="movers-news-see-all"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Newspaper className="w-4 h-4 text-primary" />
          <span className="text-[18px] font-semibold tracking-tight group-hover:text-primary transition-colors">Market Movers News</span>
          <NewsSentimentSummary articles={picks} />
          {refreshedAt && (
            <span className="text-[10px] text-muted-foreground/70 font-mono"
                  data-testid="movers-news-refreshed"
                  title={new Date(refreshedAt).toLocaleString()}>
              · Refreshed {timeAgo(new Date(refreshedAt).toISOString())}
            </span>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-[15px] text-primary group-hover:underline whitespace-nowrap font-semibold">
          See all Gainers &amp; Losers on the News Page
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </Link>
      <MarketClosedBanner note={data.market_note} compact testId="movers-news-market-closed" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
        {picks.map((a, i) => (
          <ArticleTile
            key={`${a.ticker}-${i}`}
            article={a}
            quote={(data.quotes || {})[a.ticker]}
            branding={(data.branding || {})[a.ticker]}
          />
        ))}
      </div>
    </div>
  );
}
